import React, { createContext, useContext } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';

type TBreakpointContext = {
  isMobile: boolean;
  isAboveTablet: boolean;
  isAboveDesktop: boolean;
  isAboveXL: boolean;
  isAboveXXL: boolean;
  isAboveXXXL: boolean;
  isAboveXXXXL: boolean;
};

const BreakpointContext = createContext<TBreakpointContext>({
  isMobile: false,
  isAboveTablet: false,
  isAboveDesktop: false,
  isAboveXL: false,
  isAboveXXL: false,
  isAboveXXXL: false,
  isAboveXXXXL: false,
});

export const useBreakpointContext = () => useContext(BreakpointContext);

type TBreakpointProviderProps = {
  children: React.ReactNode;
};

/**
 * When multiple components on the same page needs to know the current breakpoint,
 * using useBreakpoint directly is going to make every single component register
 * a resize event listener. This provider avoids that by only having one listener
 * and passing down the breakpoint values via context.
 */
export const BreakpointProvider = ({ children }: TBreakpointProviderProps) => {
  const {
    isMobile,
    isAboveTablet,
    isAboveDesktop,
    isAboveXL,
    isAboveXXL,
    isAboveXXXL,
    isAboveXXXXL,
  } = useBreakpoint();

  return (
    <BreakpointContext.Provider
      value={{
        isMobile,
        isAboveTablet,
        isAboveDesktop,
        isAboveXL,
        isAboveXXL,
        isAboveXXXL,
        isAboveXXXXL,
      }}>
      {children}
    </BreakpointContext.Provider>
  );
};
